import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tab,
  Tabs,
  Divider,
} from '@mui/material';
import {
  Download,
  CheckCircle,
  Assignment,
  CloudDownload,
  Info,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

interface BusinessPlanTemplate {
  id: string;
  title: string;
  authority: string;
  country: string;
  type: string;
  description: string;
  file_url: string;
  file_size: string;
  last_updated: string;
  requirements: string[];
  suitable_for: string[];
  download_count: number;
  format: string;
  official_source: string;
  local_file_path?: string;
  downloaded_at?: string;
}

const GovernmentTemplatesPage: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<BusinessPlanTemplate | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [countryFilter, setCountryFilter] = useState<'ALL' | 'US' | 'UK'>('ALL');

  // Fetch templates
  const { data: templates, isLoading } = useQuery<BusinessPlanTemplate[]>({
    queryKey: ['government-templates', countryFilter],
    queryFn: async () => {
      const params = countryFilter !== 'ALL' ? { country: countryFilter } : {};
      const response = await api.get('/template-management/templates', { params });
      return response.data;
    },
  });

  // Download template mutation
  const downloadMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const response = await api.get(`/template-management/templates/${templateId}/download`);
      return response.data;
    },
    onSuccess: (data) => {
      // Open the official source in a new tab
      if (data.download_url) {
        window.open(data.download_url, '_blank');
      }
      // Show instructions
      if (data.instructions) {
        alert(`${data.title}\n\n${data.instructions}`);
      }
    },
  });

  const handleViewDetails = (template: BusinessPlanTemplate) => {
    setSelectedTemplate(template);
    setDetailsOpen(true);
  };

  const handleDownload = async (template: BusinessPlanTemplate) => {
    try {
      await downloadMutation.mutateAsync(template.id);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const getCountryFlag = (country: string) => {
    return country === 'US' ? '🇺🇸' : country === 'UK' ? '🇬🇧' : '🌍';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Government Business Plan Templates
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Official templates from U.S. SBA and US Government for business planning and funding applications
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>100% Official:</strong> All templates are sourced directly from government agencies and
          meet official requirements for loan applications and funding programs.
        </Alert>
      </Box>

      {/* Country Filter */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={countryFilter}
          onChange={(_, newValue) => setCountryFilter(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All Templates" value="ALL" />
          <Tab label="🇺🇸 United States (SBA)" value="US" />
          <Tab label="🇬🇧 United Kingdom" value="UK" />
        </Tabs>
      </Box>

      {/* Templates Grid */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {templates?.map((template) => (
            <Grid item xs={12} md={6} lg={4} key={template.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Header */}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h2" sx={{ fontSize: 32 }}>
                      {getCountryFlag(template.country)}
                    </Typography>
                    <Chip
                      label={template.format}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={template.file_size}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>

                  {/* Title */}
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {template.title}
                  </Typography>

                  {/* Authority */}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                    <Typography variant="caption" color="success.main" fontWeight="medium">
                      {template.authority}
                    </Typography>
                  </Stack>

                  {/* Description */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {template.description}
                  </Typography>

                  {/* Stats */}
                  <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CloudDownload sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {template.download_count.toLocaleString()} downloads
                      </Typography>
                    </Stack>
                  </Stack>

                  {/* Suitable For */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Suitable for:
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {template.suitable_for.slice(0, 3).map((item, index) => (
                        <Chip
                          key={index}
                          label={item}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      ))}
                      {template.suitable_for.length > 3 && (
                        <Chip
                          label={`+${template.suitable_for.length - 3} more`}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Stack>
                  </Box>

                  {/* Downloaded Status */}
                  {template.local_file_path && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      ✓ Downloaded {new Date(template.downloaded_at!).toLocaleDateString()}
                    </Alert>
                  )}

                  {/* Actions */}
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Info />}
                      onClick={() => handleViewDetails(template)}
                      fullWidth
                    >
                      Details
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Download />}
                      onClick={() => handleDownload(template)}
                      disabled={downloadMutation.isPending}
                      fullWidth
                    >
                      {downloadMutation.isPending ? 'Downloading...' : 'Download'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedTemplate && (
          <>
            <DialogTitle>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h2" sx={{ fontSize: 32 }}>
                  {getCountryFlag(selectedTemplate.country)}
                </Typography>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedTemplate.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedTemplate.authority}
                  </Typography>
                </Box>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" paragraph>
                  {selectedTemplate.description}
                </Typography>
              </Box>

              {/* Details */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Format
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedTemplate.format}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    File Size
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedTemplate.file_size}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {new Date(selectedTemplate.last_updated).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Downloads
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedTemplate.download_count.toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Required Sections */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Required Sections
                </Typography>
                <List dense>
                  {selectedTemplate.requirements.map((req, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Assignment sx={{ fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText primary={req} />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Suitable For */}
              <Box>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Suitable For
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedTemplate.suitable_for.map((item, index) => (
                    <Chip key={index} label={item} size="small" />
                  ))}
                </Stack>
              </Box>

              {/* Official Source */}
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="caption">
                  <strong>Official Source:</strong>{' '}
                  <a
                    href={selectedTemplate.official_source}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit' }}
                  >
                    {selectedTemplate.official_source}
                  </a>
                </Typography>
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)}>Close</Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => {
                  handleDownload(selectedTemplate);
                  setDetailsOpen(false);
                }}
                disabled={downloadMutation.isPending}
              >
                Download Template
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default GovernmentTemplatesPage;
